import {
    type ReactElement,
    isValidElement,
    type ReactNode,
    Children,
    useState,
    useMemo,
    type FC
} from "react";
import {
    type NativeSyntheticEvent,
    type TargetedEvent,
    type PointerEvent,
    Pressable,
    Platform,
    View
} from "react-native";
import type IMessageProps from "./type";
import type {
    MessageFocusEventType,
    MessageWebPropsType,
    MessageContextType,
    MessageSize
} from "./type";
import stylesheet, {
    getMessageMeasures,
    useStyles
} from "./stylesheet";
import MessageActions from "./components/actions";
import MessageAvatar from "./components/avatar";
import MessageFooter from "./components/footer";
import MessageGroup from "./components/group";
import MessageHeader from "./components/header";
import MessageRow from "./components/row";
import {
    useMessageGroupContext,
    MessageContext
} from "./context";
import {
    getMessageIsFocusVisible,
    getMessageIsFocusWithin,
    getMessageActionItems,
    getMessageBubbleInfo
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    BubbleGroupContext
} from "../bubble/context";
import ActionSheet from "../bottomSheet/components/actionSheet";

const Message: FC<IMessageProps> = ({
    actionsBehaviour = "auto",
    isStreaming = false,
    isLatest = false,
    isAvatarVisible,
    size = "medium",
    customLocalize,
    deliveryStatus,
    onPointerEnter,
    onPointerLeave,
    groupPosition,
    customTheme,
    onLongPress,
    timestamp,
    children,
    onFocus,
    onRetry,
    avatar,
    onBlur,
    align,
    style,
    title,
    ...props
}) => {
    const groupContext = useMessageGroupContext();

    const currentTheme = customTheme ?? groupContext?.customTheme;

    const currentLocalize = customLocalize ?? groupContext?.customLocalize;

    const {
        controlHeights,
        shapeConfigs,
        avatarSizes,
        typography,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(currentTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(currentLocalize);

    const pointerType = usePointerType();

    const [
        isRootFocused,
        setIsRootFocused
    ] = useState(false);

    const [
        isFocusWithin,
        setIsFocusWithin
    ] = useState(false);

    const [
        isPointerInside,
        setIsPointerInside
    ] = useState(false);

    const [
        isSheetActive,
        setIsSheetActive
    ] = useState(false);

    const currentAlign = align ?? groupContext?.align ?? "start";

    const currentPosition = groupPosition ?? groupContext?.position ?? "single";

    const currentAvatar = avatar ?? groupContext?.avatar;

    const currentTitle = title ?? groupContext?.title;

    const currentSize: MessageSize = size;

    const onFocusRoot = (event: NativeSyntheticEvent<TargetedEvent>) => {
        setIsRootFocused(event.target === event.currentTarget);

        onFocus?.(event);
    };

    const onBlurRoot = (event: NativeSyntheticEvent<TargetedEvent>) => {
        setIsRootFocused(false);

        onBlur?.(event);
    };

    const onPointerEnterRoot = (event: PointerEvent) => {
        if(Platform.OS === "web") {
            setIsPointerInside(true);
        }

        onPointerEnter?.(event);
    };

    const onPointerLeaveRoot = (event: PointerEvent) => {
        if(Platform.OS === "web") {
            setIsPointerInside(false);
        }

        onPointerLeave?.(event);
    };

    const onFocusWithin = (event: MessageFocusEventType) => {
        setIsFocusWithin(getMessageIsFocusVisible(event.target));
    };

    const onBlurWithin = (event: MessageFocusEventType) => {
        if(getMessageIsFocusWithin({
            relatedTarget: event.relatedTarget,
            currentTarget: event.currentTarget
        })) {
            return;
        }

        setIsFocusWithin(false);
    };

    const webProps: MessageWebPropsType = Platform.OS === "web" ? {
        onFocus: onFocusWithin,
        onBlur: onBlurWithin
    } : {};

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isFocused
    } = useInteractionState({
        onFocus: onFocusRoot,
        onBlur: onBlurRoot
    });

    const items = Children.toArray(children);

    const headerChild = items.find((child) => isValidElement(child) && child.type === MessageHeader);

    const actionsChild = items.find((child): child is ReactElement<{
        children?: ReactNode;
    }> => isValidElement(child) && child.type === MessageActions);

    const footerChild = items.find((child) => isValidElement(child) && child.type === MessageFooter);

    const rowChildren = items.filter((child) => isValidElement(child) && child.type === MessageRow);

    const bodyChildren = items.filter((child) => {
        return child !== headerChild && child !== actionsChild && child !== footerChild && !rowChildren.includes(child);
    });

    const bubbleInfo = getMessageBubbleInfo(bodyChildren);

    const isPlain = bubbleInfo.variant === "plain";

    const isSystem = bubbleInfo.variant === "system";

    const isLastOfGroup = currentPosition === "last" || currentPosition === "single";

    const isSlotReserved = (isAvatarVisible ?? currentAlign === "start") && !isPlain && !isSystem && Boolean(currentAvatar);

    const isAvatarDrawn = isSlotReserved && isLastOfGroup;

    const isActionsEnabled = actionsBehaviour !== "none" && Boolean(actionsChild) && pointerType === "fine";

    const isActionsVisible = actionsBehaviour === "always" || isLatest || isHovered || isFocused || isFocusWithin || isPointerInside;

    const actionItems = useMemo(() => {
        return getMessageActionItems(actionsChild?.props.children);
    }, [
        actionsChild
    ]);

    const measures = getMessageMeasures({
        isHasFooter: Boolean(footerChild) && isLastOfGroup,
        isHasReactions: bubbleInfo.isHasReactions,
        size: currentSize,
        controlHeights,
        shapeConfigs,
        avatarSizes,
        typography,
        spaces
    });

    const {
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        avatar: avatarDynamicStyle,
        rows: rowsDynamicStyle
    } = useStyles({
        isFocusVisible: isFocusVisible && isRootFocused,
        isAvatarSlot: isSlotReserved,
        align: currentAlign,
        shapeConfigs,
        isSystem,
        measures,
        radiuses,
        borders,
        colors,
        spaces
    });

    const actions = isActionsEnabled ? actionsChild : undefined;

    const context = useMemo((): MessageContextType => ({
        bubbleVariant: bubbleInfo.variant,
        customLocalize: currentLocalize,
        groupPosition: currentPosition,
        customTheme: currentTheme,
        avatar: currentAvatar,
        align: currentAlign,
        title: currentTitle,
        size: currentSize,
        isActionsVisible,
        deliveryStatus,
        isAvatarDrawn,
        isStreaming,
        timestamp,
        isSystem,
        actions,
        isPlain,
        onRetry
    }), [
        bubbleInfo.variant,
        currentLocalize,
        currentPosition,
        currentTheme,
        currentAvatar,
        currentAlign,
        currentTitle,
        currentSize,
        isActionsVisible,
        deliveryStatus,
        isAvatarDrawn,
        isStreaming,
        timestamp,
        isSystem,
        isPlain,
        actions,
        onRetry
    ]);

    const bubbleGroupValue = useMemo(() => ({
        position: currentPosition,
        align: currentAlign
    }), [
        currentPosition,
        currentAlign
    ]);

    const handleLongPress = () => {
        if(pointerType !== "coarse") {
            return;
        }

        if(onLongPress) {
            onLongPress();

            return;
        }

        if(actionsBehaviour !== "none" && actionItems.length) {
            setIsSheetActive(true);
        }
    };

    const handleSheetClosed = () => {
        setIsSheetActive(false);
    };

    const renderAvatar = () => {
        if(!isSlotReserved) {
            return null;
        }

        return <View
            aria-hidden={!isAvatarDrawn}
            style={[
                stylesheet.avatar,
                avatarDynamicStyle
            ]}
        >
            {
                isAvatarDrawn ?
                    <MessageAvatar />
                :
                    null
            }
        </View>;
    };

    const renderRows = () => {
        if(!rowChildren.length) {
            return <MessageRow>
                {bodyChildren}
            </MessageRow>;
        }

        return <View
            style={[
                stylesheet.rows,
                rowsDynamicStyle
            ]}
        >
            {rowChildren}
            {bodyChildren}
        </View>;
    };

    const renderContent = () => {
        return <BubbleGroupContext.Provider
            value={bubbleGroupValue}
        >
            {headerChild ?? <MessageHeader />}
            {renderRows()}
            {footerChild ?? <MessageFooter />}
        </BubbleGroupContext.Provider>;
    };

    const renderSheet = () => {
        if(actionsBehaviour === "none" || !actionItems.length || pointerType !== "coarse") {
            return null;
        }

        return <ActionSheet
            title={currentTitle ?? localize("message-actions")}
            items={actionItems.map((item) => ({
                type: item.isDanger ? "danger" : "neutral",
                onPress: item.onPress,
                label: item.label,
                icon: item.icon,
                key: item.key
            }))}
            customLocalize={currentLocalize}
            onClosed={handleSheetClosed}
            customTheme={currentTheme}
            isActive={isSheetActive}
        />;
    };

    return <MessageContext.Provider
        value={context}
    >
        <Pressable
            {...props}
            delayLongPress={durations.longPress}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            {...interactionHandlers}
            onPointerLeave={onPointerLeaveRoot}
            onPointerEnter={onPointerEnterRoot}
            onLongPress={handleLongPress}
            aria-label={currentTitle}
            aria-busy={isStreaming}
            role="article"
            tabIndex={-1}
        >
            {renderAvatar()}
            <View
                {...webProps}
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderContent()}
            </View>
        </Pressable>
        {renderSheet()}
    </MessageContext.Provider>;
};
export default Object.assign(Message, {
    Actions: MessageActions,
    Avatar: MessageAvatar,
    Footer: MessageFooter,
    Header: MessageHeader,
    Group: MessageGroup,
    Row: MessageRow
});
